import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Search,
    Filter,
    Trash2,
    Heart,
    Share2,
    MapPin,
    Bed,
    Bath,
    Car,
    Square,
    Calendar,
    ExternalLink,
    Plus,
    ChevronDown,
    ChevronUp,
    Grid,
    Grid3x3,
    List,
    Edit,
    Home,
    Building2,
    Building,
    Trees,
    Tractor,
    Layers,
    TrendingUp,
    X,
    Circle,
    Check,
    Zap,
    Coins,
    ArrowRight,
    Maximize2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { updateCollingsProperty, deleteCollingsProperty } from "../../../api/collingsProperties";
import { getPublicProperties, patchPublicProperty } from "../../../api/publicApi";
import { deleteProperty, updateProperty, patchProperty } from "../../../api/agent";
import moment from "moment";
import toast from "react-hot-toast";
import { capitalizeEachWord } from "../../../helper/function";
import { formatShortAddress } from "../../../utils/addressFormatter";
import SkeletonPropertyCard from "../../../components/search/SkeletonPropertyCard";
import { invalidateCache } from "../../../lib/cache";
import LazyImage from "../../../components/common/LazyImage";
import ManualSearchOverlay from "../components/ManualSearchOverlay";
import PropertySearchBar from "../components/PropertySearchBar";
import PropertyCard from "../components/PropertyCard";
import PropertyGridView from "../components/PropertyGridView";
import PropertyListView from "../components/PropertyListView";
import ConfirmModal from "../../../components/common/ConfirmModal";
import MobileSortDropdown from "../components/MobileSortDropdown";
import { hasAdminAccess } from "../../../utils/roleAccess";


const PublicPropertiesPage = () => {
    const { user } = useSelector((state) => state.auth);

    const isAdmin = useMemo(() => hasAdminAccess(user), [user]);
    const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
    const [searchTerm, setSearchTerm] = useState("");
    const [properties, setProperties] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;
    const queryFiltersRef = useRef({});
    const loadMoreSentinelRef = useRef(null);
    const [deletingId, setDeletingId] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editProperty, setEditProperty] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [propertyToDelete, setPropertyToDelete] = useState(null);
    const navigate = useNavigate();
    const searchTimeout = useRef();
    const [imageIndexes, setImageIndexes] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);


    // Filter states
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        listingType: 'all', // 'all', 'public', 'offmarket'
        suburb: '',
        minBeds: '',
        minBaths: '',
        minCarSpaces: '',
        priceMin: '',
        priceMax: ''
    });

    // New state for UI revamp
    const [selectedMethods, setSelectedMethods] = useState(["all"]);
    const [selectedListingTypes, setSelectedListingTypes] = useState(["sales"]); // Default to For Sale; user can switch to For Lease

    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    // Advanced Filter State
    const [buyerType, setBuyerType] = useState("owner");
    const [advFilters, setAdvFilters] = useState({
        propertyTypes: ["all"],
        priceRange: { min: "Any", max: "Any" },
        bedrooms: { min: "Any", max: "Any" },
        useBedroomRange: false,
        bathrooms: "Any",
        carSpaces: "Any",
        landSize: { min: "Any", max: "Any" },
        saleMethod: "All",
        excludeUnderContract: false,
        targetYield: "Any",
        capitalGrowth: "Any",
        showWithPriceOnly: false,
    });

    const handleToggleFilter = (current, setFunction, value) => {
        if (current.includes(value)) {
            const next = current.filter(v => v !== value);
            if (next.length === 0) {
                setFunction(["all"]);
            } else {
                setFunction(next);
            }
        } else {
            setFunction([...current, value]);
        }
    };

    const getDaysBadgeColorLocal = (days) => {
        if (days <= 14) return "bg-[#00D094] text-white";
        if (days <= 30) return "bg-[#FF8E26] text-white";
        return "bg-[#EE3E34] text-white";
    };

    const getBorderColorLocal = (days) => {
        if (days <= 14) return "border-[#00D094]";
        if (days <= 30) return "border-[#FF8E26]";
        return "border-[#EE3E34]";
    };

    const handleSort = (key, explicitDirection = null) => {
        let direction = explicitDirection;
        if (!direction) {
            direction = "asc";
            if (sortConfig.key === key && sortConfig.direction === "asc") {
                direction = "desc";
            }
        }
        setSortConfig({ key, direction });
    };

    const transformPublicItems = (rawItems) => {
        return rawItems.map(item => ({
            ...item,
            address: formatShortAddress(item),
            price: item.price,
            price_from: item.price_from ?? item.price,
            price_to: item.price_to,
            price_display: item.price_display || 'exact',
            title: item.title,
            beds: item.beds ?? 0,
            baths: item.baths ?? 0,
            parkings: item.parkings ?? 0,
            car_ports: item.car_ports ?? 0,
            open_spaces: item.open_spaces ?? 0,
            images: item.images || [],
            property_type: item.property_type,
            created_at: item.created_at,
            weekly_rent: item.weekly_rent ?? null,
            is_sold: item.is_sold ?? false,
            est_gross_yield: item.est_gross_yield || null,
            est_outgoings: item.est_outgoings || null,
            est_net_yield: item.est_net_yield || null
        }));
    };

    const fetchProperties = async (page = 1, filters = {}, options = {}) => {
        const { append = false } = options;
        if (!append) {
            setLoading(true);
            setError(null);
        } else {
            setLoadingMore(true);
        }
        try {
            const params = { page, page_size: itemsPerPage, ...filters };
            const res = await getPublicProperties(params);
            const data = res.data;
            const rawItems = Array.isArray(data?.results) ? data.results : [];
            const items = transformPublicItems(rawItems);

            if (append) {
                setProperties(prev => [...prev, ...items]);
            } else {
                setProperties(items);
            }
            setNextPage(data?.next || null);
            setCurrentPage(data?.page || page);
            if (data?.count !== undefined) setTotalCount(data.count);
        } catch (err) {
            setError("Failed to load properties");
            toast.error("Failed to load properties");
        } finally {
            if (append) setLoadingMore(false);
            else setLoading(false);
        }
    };

    const loadMore = useCallback(() => {
        if (loadingMore || loading || !nextPage) return;
        fetchProperties(currentPage + 1, queryFiltersRef.current, { append: true });
    }, [currentPage, nextPage, loadingMore, loading]);

    const filteredProperties = useMemo(() => {
        let result = [...properties];

        if (filters.suburb) {
            const suburb = filters.suburb.toLowerCase();
            result = result.filter(p =>
                (p.address || "").toLowerCase().includes(suburb) ||
                (p.title || "").toLowerCase().includes(suburb)
            );
        }

        if (!advFilters.propertyTypes.includes("all")) {
            result = result.filter(p => {
                const type = (p.property_type || "").toLowerCase();
                return advFilters.propertyTypes.some(t => type.includes(t.toLowerCase()));
            });
        }

        if (advFilters.priceRange.min !== "Any") {
            const min = parseFloat(advFilters.priceRange.min);
            result = result.filter(p => {
                const price = typeof p.price === 'string' ? parseFloat(p.price.replace(/[^0-9.-]+/g, '')) : p.price;
                return price >= min;
            });
        }

        if (advFilters.priceRange.max !== "Any") {
            const max = parseFloat(advFilters.priceRange.max);
            result = result.filter(p => {
                const price = typeof p.price === 'string' ? parseFloat(p.price.replace(/[^0-9.-]+/g, '')) : p.price;
                return price <= max;
            });
        }

        if (advFilters.bedrooms.min !== "Any") {
            result = result.filter(p => (p.beds || 0) >= parseInt(advFilters.bedrooms.min));
        }
        if (advFilters.bedrooms.max !== "Any") {
            result = result.filter(p => (p.beds || 0) <= parseInt(advFilters.bedrooms.max));
        }

        if (advFilters.bathrooms !== "Any") {
            result = result.filter(p => (p.baths || 0) >= parseInt(advFilters.bathrooms));
        }

        if (advFilters.carSpaces !== "Any") {
            result = result.filter(p => (p.parkings || 0) >= parseInt(advFilters.carSpaces));
        }

        if (advFilters.saleMethod !== "All") {
            result = result.filter(p => {
                const method = (p.method_of_sale || p.status || "").toLowerCase();
                return method.includes(advFilters.saleMethod.toLowerCase());
            });
        }

        if (advFilters.excludeUnderContract) {
            result = result.filter(p => (p.status || "").toLowerCase() !== "under contract");
        }

        if (advFilters.showWithPriceOnly) {
            result = result.filter(p => p.price && p.price !== "0");
        }

        // Filter by Methods (On Market / Off Market)
        if (!selectedMethods.includes("all")) {
            result = result.filter(p => {
                const statusName = (p.status || "").toLowerCase();
                const source = (p.source || "").toLowerCase();
                if (selectedMethods.includes("on_market") && (statusName.includes("on market") || source === "crm" || source === "rea" || source === "collings-data")) return true;
                if (selectedMethods.includes("off_market") && statusName.includes("off market")) return true;
                return false;
            });
        }

        // Filter by Listing Type (Sales / Lease)
        if (!selectedListingTypes.includes("all")) {
            result = result.filter(p => {
                const statusName = (p.status || "").toLowerCase();
                const category = (p.property_type || "").toLowerCase();
                if (selectedListingTypes.includes("sales") && statusName !== 'rental' && !statusName.includes('lease') && !category.includes('lease')) return true;
                if (selectedListingTypes.includes("lease") && (statusName === 'rental' || statusName.includes('lease') || category.includes('lease'))) return true;
                return false;
            });
        }


        // Exclude Sold properties unless the user is actively searching
        if (!searchTerm) {
            result = result.filter(p => {
                const isSold = p.is_sold ||
                    (p.status || "").toLowerCase() === "sold" ||
                    (p.status?.name || "").toLowerCase() === "sold";
                return !isSold;
            });
        }

        if (sortConfig.key) {
            result.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Map sort keys to actual property keys if needed
                if (sortConfig.key === 'parking') {
                    aValue = a.parkings || a.parking;
                    bValue = b.parkings || b.parking;
                } else if (sortConfig.key === 'type') {
                    aValue = a.property_type || a.type;
                    bValue = b.property_type || b.type;
                } else if (sortConfig.key === 'area') {
                    aValue = a.floor_area || a.area || a.building_area;
                    bValue = b.floor_area || b.area || b.building_area;
                } else if (sortConfig.key === 'gross_yield') {
                    aValue = a.gross_yield || a.grossYield || a.est_gross_yield;
                    bValue = b.gross_yield || b.grossYield || b.est_gross_yield;
                } else if (sortConfig.key === 'net_yield') {
                    aValue = a.net_yield || a.netYield || a.est_net_yield;
                    bValue = b.net_yield || b.netYield || b.est_net_yield;
                }

                // Handle numeric string conversions
                const numericKeys = ['price', 'beds', 'baths', 'parkings', 'area', 'land_area', 'rent', 'gross_yield', 'net_yield'];
                if (numericKeys.includes(sortConfig.key) || sortConfig.key === 'parking' || sortConfig.key === 'floor_area') {
                    const parseVal = (val) => {
                        if (typeof val === 'number') return val;
                        if (!val) return 0;
                        if (typeof val === 'string') {
                            return parseFloat(val.replace(/[^0-9.-]+/g, '')) || 0;
                        }
                        return 0;
                    };
                    aValue = parseVal(aValue);
                    bValue = parseVal(bValue);
                }

                if ((aValue || 0) > (bValue || 0)) return sortConfig.direction === "asc" ? 1 : -1;
                if ((aValue || 0) < (bValue || 0)) return sortConfig.direction === "asc" ? -1 : 1;
                return 0;
            });
        }

        return result;
    }, [properties, advFilters, filters, sortConfig, selectedMethods, selectedListingTypes, searchTerm]);

    const handleClearFilters = () => {
        setFilters({
            listingType: 'all',
            suburb: '',
            minBeds: '',
            minBaths: '',
            minCarSpaces: '',
            priceMin: '',
            priceMax: ''
        });
        setAdvFilters({
            propertyTypes: ["all"],
            priceRange: { min: "Any", max: "Any" },
            bedrooms: { min: "Any", max: "Any" },
            useBedroomRange: false,
            bathrooms: "Any",
            carSpaces: "Any",
            landSize: { min: "Any", max: "Any" },
            saleMethod: "All",
            excludeUnderContract: false,
            targetYield: "Any",
            capitalGrowth: "Any",
            showWithPriceOnly: false,
        });
        setSearchTerm("");
        setSelectedMethods(["all"]);
        setSelectedListingTypes(["sales"]);
        setCurrentPage(1);
    };

    const paginatedProperties = filteredProperties;

    const buildQueryFilters = useCallback(() => {
        const queryFilters = {};
        if (searchTerm.trim()) queryFilters.search = searchTerm.trim();
        if (advFilters.priceRange.min !== "Any") queryFilters.min_price = advFilters.priceRange.min;
        if (advFilters.priceRange.max !== "Any") queryFilters.max_price = advFilters.priceRange.max;
        if (advFilters.bedrooms.min !== "Any") queryFilters.beds = advFilters.bedrooms.min;
        if (!advFilters.propertyTypes.includes("all")) queryFilters.property_type = advFilters.propertyTypes[0];
        if (filters.suburb?.trim()) queryFilters.city = filters.suburb.trim();
        if (!selectedListingTypes.includes("all")) queryFilters.listing_type = selectedListingTypes[0];
        return queryFilters;
    }, [searchTerm, advFilters.priceRange, advFilters.bedrooms, advFilters.propertyTypes, filters.suburb, selectedListingTypes]);

    useEffect(() => {
        setCurrentPage(1);
        setProperties([]);
        const queryFilters = buildQueryFilters();
        queryFiltersRef.current = queryFilters;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => fetchProperties(1, queryFilters), 400);
        return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
    }, [searchTerm, advFilters, filters, buyerType, selectedMethods, selectedListingTypes, buildQueryFilters]);

    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        if (!sentinel || !nextPage) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && nextPage && !loadingMore && !loading) loadMore();
            },
            { root: null, rootMargin: "200px", threshold: 0.1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [nextPage, loadingMore, loading, loadMore]);

    const handleEdit = (property) => {
        navigate(`/public-property-search/edit/${property.id}`);
    };

    const handleDeleteClick = (property) => {
        setPropertyToDelete(property);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!propertyToDelete) return;
        
        const deletedId = propertyToDelete.id;
        setDeletingId(deletedId);
        try {
            await deleteProperty(deletedId);
            
            // Immediately remove from local state (no refresh needed)
            setProperties(prev => prev.filter(p => p.id !== deletedId));
            setTotalCount(prev => Math.max(0, prev - 1));
            
            toast.success('Property deleted successfully');
            setShowDeleteModal(false);
            setPropertyToDelete(null);
            
            // Invalidate cache for future fetches
            invalidateCache(`public-properties-`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to delete property');
        } finally {
            setDeletingId(null);
        }
    };

    const handleStageUpdate = async (propertyId, newStage) => {
        try {
            // Determine if it's a Collings/REA property or a CRM property
            const isCollings = propertyId.toString().startsWith('rea-');

            if (isCollings) {
                const { patchCollingsProperty } = await import('../../../api/collingsProperties');
                await patchCollingsProperty(propertyId, { status: newStage });
            } else {
                await patchPublicProperty(propertyId, { status: newStage });
            }

            toast.success("Stage updated successfully");
            setProperties(prev => prev.map(p =>
                p.id === propertyId ? { ...p, status: newStage } : p
            ));
            invalidateCache(`public-properties-`);
        } catch (err) {
            toast.error("Failed to update stage");
        }
    };

    const getDaysOnMarket = (createdAt) => {
        if (!createdAt) return 0;
        const created = moment(createdAt);
        const now = moment();
        return now.diff(created, 'days');
    };

    return (
        <div className="min-h-screen bg-[#FFFFFF] p-0 sm:p-0 text-[#36423F] -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6 w-auto max-w-full overflow-x-hidden">
            <div className="bg-[#F8F8F8] px-4 sm:px-6 md:px-6 lg:px-8 py-4 sm:py-5 md:py-4 lg:py-5 border-b border-gray-100">
                <div className="flex flex-col gap-3 sm:gap-4 md:gap-3 lg:gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                        <h1 className="text-2xl sm:text-3xl font-bold text-[#55C5D0]">Public Properties</h1>

                        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                            <MobileSortDropdown
                                sortConfig={sortConfig}
                                onSortChange={handleSort}
                                disabled={loading}
                                className="lg:hidden"
                            />
                            <div className="bg-[#EFEFEF] p-1 rounded-full flex items-center h-10 sm:h-12">
                                <button
                                    onClick={() => setViewMode("list")}
                                    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 rounded-full transition-all text-xs sm:text-sm ${viewMode === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                >
                                    <List className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span className="font-bold">List</span>
                                </button>
                                <button
                                    onClick={() => setViewMode("grid")}
                                    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 rounded-full transition-all text-xs sm:text-sm ${viewMode === "grid" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                >
                                    <Grid3x3 className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span className="font-bold">Grid</span>
                                </button>
                            </div>

                            <button
                                onClick={() => navigate('/public-property-search/add')}
                                className="bg-[#36423F] hover:bg-[#2d3a37] text-white h-10 sm:h-12 px-4 sm:px-8 rounded-full font-bold shadow-lg transition-all flex items-center gap-2 active:scale-95 whitespace-nowrap text-xs sm:text-sm"
                            >
                                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                                <span>Add Property</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-x-6 lg:gap-x-8 md:gap-y-0">
                        <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:gap-3">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider md:shrink-0 md:w-[100px]">Method of Sale</span>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-2">
                                {[
                                    { id: "all", label: "All" },
                                    { id: "on_market", label: "On Market" },
                                    { id: "off_market", label: "Off Market" }
                                ].map((option) => (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            // Always replace the entire array with just the selected option
                                            // This ensures only one filter is active at a time
                                            setSelectedMethods([option.id]);
                                        }}
                                        className={`px-4 sm:px-6 md:px-4 py-2 sm:py-2.5 md:py-2 rounded-lg text-xs sm:text-sm font-bold transition-all border ${selectedMethods.length === 1 && selectedMethods[0] === option.id
                                            ? "bg-[#55C5D0] border-[#55C5D0] text-white shadow-sm"
                                            : "bg-white border-gray-200 text-[#36423F] hover:border-[#55C5D0] hover:text-[#55C5D0]"
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:gap-3">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider md:shrink-0 md:w-[100px]">Listing Type</span>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-2">
                                {[
                                    { id: "sales", label: "For Sale" },
                                    { id: "lease", label: "For Lease" }
                                ].map((option) => (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            // Always replace the entire array with just the selected option
                                            // This ensures only one filter is active at a time
                                            setSelectedListingTypes([option.id]);
                                        }}
                                        className={`px-4 sm:px-6 md:px-4 py-2 sm:py-2.5 md:py-2 rounded-lg text-xs sm:text-sm font-bold transition-all border ${selectedListingTypes.length === 1 && selectedListingTypes[0] === option.id
                                            ? "bg-[#55C5D0] border-[#55C5D0] text-white shadow-sm"
                                            : "bg-white border-gray-200 text-[#36423F] hover:border-[#55C5D0] hover:text-[#55C5D0]"
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 sm:px-6 py-4 sm:py-6 md:py-5 w-full overflow-hidden">
                <div className="relative mb-10">
                    <PropertySearchBar
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onSearchClick={() => fetchProperties(1, { search: searchTerm })}
                        onFilterClick={() => setShowFilters(!showFilters)}
                        showFilters={showFilters}
                        placeholder="Search Address or Property Features with AI"
                        placeholderMobile="Search address or features"
                        searchButtonLabel="Search"
                    />
                    <ManualSearchOverlay
                        showFilters={showFilters}
                        setShowFilters={setShowFilters}
                        buyerType={buyerType}
                        setBuyerType={setBuyerType}
                        advFilters={advFilters}
                        setAdvFilters={setAdvFilters}
                        filters={filters}
                        setFilters={setFilters}
                        onClear={handleClearFilters}
                    />
                </div>

                <div className="mb-10">
                    {loading && !error ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 animate-pulse">
                                    <div className="h-48 bg-gray-100 rounded-lg mb-4"></div>
                                    <div className="h-4 bg-gray-100 rounded w-3/4 mb-2"></div>
                                    <div className="h-3 bg-gray-100 rounded w-1/2 mb-4"></div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <div className="text-red-500 mb-2">Error loading properties ({error})</div>
                            <button onClick={() => fetchProperties(1, {})} className="text-[#55C5D0] font-bold">Try again</button>
                        </div>
                    ) : filteredProperties.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-dashed border-gray-200">
                            <span className="text-5xl mb-4">🏠</span>
                            <span className="text-gray-900 text-xl font-bold mb-2">No properties found</span>
                            <p className="text-gray-400">Try adjusting your filters or search terms</p>
                        </div>
                    ) : viewMode === "grid" ? (
                        <PropertyGridView
                            properties={paginatedProperties}
                            getDaysOnMarket={getDaysOnMarket}
                            basePath="/public-property-search"
                            onEdit={handleEdit}
                            onDelete={handleDeleteClick}
                        />

                    ) : (
                        <PropertyListView
                            properties={paginatedProperties}
                            handleSort={handleSort}
                            navigate={navigate}
                            capitalizeEachWord={capitalizeEachWord}
                            onEdit={handleEdit}
                            sortConfig={sortConfig}
                            onStageUpdate={handleStageUpdate}
                            basePath="/public-property-search"
                        />
                    )}
                </div>

                {totalCount > 0 && (
                    <div className="flex justify-center mt-6">
                        <span className="text-sm text-gray-500">
                            Showing {filteredProperties.length} of {totalCount} properties
                        </span>
                    </div>
                )}

                {nextPage && !error && filteredProperties.length > 0 && (
                    <div ref={loadMoreSentinelRef} className="h-10 flex items-center justify-center mt-6" aria-hidden="true" />
                )}

                {loadingMore && (
                    <div className="flex items-center justify-center py-6 gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#55C5D0] border-t-transparent" />
                        <span className="text-sm text-gray-500">Loading more properties...</span>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setPropertyToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                loading={deletingId === propertyToDelete?.id}
                title="Delete Property"
                description={`Are you sure you want to permanently delete "${propertyToDelete?.address || propertyToDelete?.title || 'this property'}"? This action cannot be undone and all associated data will be removed.`}
                confirmText="Delete Property"
                variant="danger"
            />

        </div >
    );
};

export default PublicPropertiesPage;
